import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Instant, LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { BodyOf, ResponseOf } from "../../../core/api";
import { auth } from "../../../core/auth";
import useApi from "../../../shared/hooks/useApi";
import { components, ExternalPatientServicePlanId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";
import { GET_CO_SERVICE_PLAN_IMPORTS_QUERY } from "../service-plan.graphql";
import CoServicePlanEngineTag from "./CoServicePlanEngineTag";
import CoServicePlanImportStatusTag, { Status } from "./CoServicePlanImportStatusTag";
import CoServicePlanScheduleEditorModal from "./CoServicePlanScheduleEditorModal";
import ServicePlanViolationList from "./ServicePlanViolationList";
import { DAYS, DAYS_MONDAY_FIRST, dayIndex, hours, title } from "./servicePlanWeek.utils";

interface Props {
  importId: ExternalPatientServicePlanId | null;
  onClose: () => void;
}

export default function CoServicePlanImportDrawer(props: Props) {
  return (
    <Drawer isOpen={props.importId !== null} size="xl" onClose={props.onClose}>
      <DrawerOverlay zIndex="aboveLegacyFooter" />
      {/* Wider than Chakra's `xl` (~896px), which crowded the two comparison
          tables and the eight-column duties table into wrapping. Chakra offers
          nothing between `xl` and `full`, and `full` would hide the imports list
          this opens from — so the width is set here instead. The `vw` cap keeps a
          sliver of that list visible on a wide screen and lets the drawer shrink
          on a narrow one. */}
      <DrawerContent containerProps={{ zIndex: "aboveLegacyFooter" }} maxW="min(1400px, 92vw)">
        <DrawerCloseButton />
        {props.importId !== null && <DrawerInner importId={props.importId} />}
      </DrawerContent>
    </Drawer>
  );
}

/** The statuses a coordinator can still act on, and what they can do to each. */
const CAN_APPROVE = "PENDING_APPROVAL";
const CAN_REGENERATE: Status[] = ["PENDING_APPROVAL", "NEEDS_REVIEW", "DECLINED"];

/**
 * How each kind of proposed change a past plan's week makes is labelled. A past plan's
 * week is now built by the LP solver, which only reports what it read the week from
 * (ONE_OFF_LEFT_OUT, WEEKS_DIFFER) and what approval keeps (OTHER_LINES_KEPT); the rest
 * come from the allocation into the existing visits that built it before, and are still
 * on weeks staged then.
 */
const CHANGE_KIND_LABELS: Record<CoServicePlanImportProposedChange["kind"], string> = {
  EXTENDED: "Extended",
  ADDED: "Added",
  SNAPPED: "Adjusted",
  STAFFED: "Staffed",
  // Legacy: no longer produced (an existing visit with no duties is now kept), but
  // weeks staged before still carry it.
  REMOVED: "Removed",
  UNSCHEDULABLE: "Unstaffed",
  UNALLOCATED: "Not allocated",
  SCHEDULE_DIFFERS: "Differs from plan",
  OTHER_LINES_KEPT: "Other line kept",
  ONE_OFF_LEFT_OUT: "One-off left out",
  WEEKS_DIFFER: "Weeks differ",
};

/**
 * Among the kinds a coordinator should stop at: red where approval removes visits that
 * are not part of the new week, orange where some of the plan's care is not delivered --
 * which includes a visit nobody can staff or bill (UNSCHEDULABLE), even though approval
 * removes that visit too -- and yellow where what was read disagrees with the plan or
 * with itself. Gray is a minor adjustment or nothing changing at all.
 */
const CHANGE_KIND_COLORS: Record<keyof typeof CHANGE_KIND_LABELS, string> = {
  EXTENDED: "blue",
  ADDED: "green",
  SNAPPED: "gray",
  STAFFED: "teal",
  REMOVED: "red",
  UNSCHEDULABLE: "orange",
  UNALLOCATED: "orange",
  SCHEDULE_DIFFERS: "yellow",
  // Visits on a line the plan does not name are left exactly as they are.
  OTHER_LINES_KEPT: "gray",
  // Approval removes these visits, as it did a REMOVED one.
  ONE_OFF_LEFT_OUT: "red",
  WEEKS_DIFFER: "yellow",
};

/**
 * The badge a proposed change is shown under.
 *
 * By kind, except that a visit left out because its line has no authorization says
 * so: "Unstaffed" would send a coordinator looking for a caregiver when what is
 * missing is the payer's approval.
 */
function changeLabel(change: CoServicePlanImportProposedChange) {
  if (change.kind === "UNSCHEDULABLE" && change.reason === "NO_AUTHORIZATION") {
    return "No authorization";
  }
  return CHANGE_KIND_LABELS[change.kind];
}

const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Approve, decline, or ask for another schedule.
 *
 * The only place in the product where a service plan import becomes real. Until a
 * coordinator presses Approve the pipeline has written a plan of care that is
 * unsubmitted and nothing else — no weekly template, no visits — so the copy here
 * has to make clear that this is the moment the patient's calendar changes.
 *
 * Regenerating is the way out of a proposal a coordinator cannot use, and it is
 * offered after a failure and after a decline as well, which is where a second
 * attempt is most often wanted. It never touches the patient either.
 */
function ReviewActions(props: {
  importId: ExternalPatientServicePlanId;
  isPastPlan: boolean;
  status: Status;
  /** When approving would take effect: a choice of two days, or the one day. Null when nothing awaits approval. */
  approvalStart: CoServicePlanApprovalStart | null;
}) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const declineForm = useDisclosure();
  const regenerateConfirm = useDisclosure();
  const [notes, setNotes] = React.useState("");

  const refresh = () => {
    // Both the drawer and the list behind it: the row's status tag is wrong the
    // moment this succeeds.
    queryClient.invalidateQueries({
      queryKey: queries.servicePlan.coImportDetails(props.importId).queryKey,
    });
    queryClient.invalidateQueries({ queryKey: [GET_CO_SERVICE_PLAN_IMPORTS_QUERY] });
  };

  const failed = (title: string) => (error: unknown) =>
    toast({
      title,
      description: formatErrorResponse(error),
      status: "error",
      position: "top-right",
      isClosable: true,
    });

  const approve = useMutation({
    ...mutations.servicePlan.approveCoImport(),
    onSuccess: (result, variables) => {
      refresh();
      const startsOn = approvalStartDate(props.approvalStart, variables.startFrom);
      toast({
        title:
          startsOn === null ? "Schedule approved" : `Schedule approved from ${fmtDate(startsOn)}`,
        description:
          `${result.recurringVisitsCreated} visits created from ` +
          `${result.templateShiftsCreated} weekly shifts.` +
          (result.warnings.length > 0 ? ` ${result.warnings.length} warning(s).` : ""),
        status: result.warnings.length > 0 ? "warning" : "success",
        position: "top-right",
        isClosable: true,
        duration: result.warnings.length > 0 ? null : undefined,
      });
    },
    onError: failed("Could not approve this schedule"),
  });

  const decline = useMutation({
    ...mutations.servicePlan.declineCoImport(),
    onSuccess: () => {
      refresh();
      declineForm.onClose();
      setNotes("");
      toast({
        title: "Schedule declined",
        description: "Nothing was changed for this patient.",
        status: "info",
        position: "top-right",
        isClosable: true,
      });
    },
    onError: failed("Could not decline this schedule"),
  });

  const regenerate = useMutation({
    ...mutations.servicePlan.regenerateCoImport(),
    onSuccess: () => {
      refresh();
      regenerateConfirm.onClose();
      toast({
        title: "Building another schedule",
        description:
          "The plan is back on the queue. This usually takes a few minutes; the status " +
          "here will change when it is done.",
        status: "info",
        position: "top-right",
        isClosable: true,
      });
    },
    onError: failed("Could not ask for another schedule"),
  });

  const canApprove = props.status === CAN_APPROVE;
  // Both kinds are built by the LP solver. A past plan's week is laid on the days and
  // hours of the patient's existing visits and has no engine to choose, so it names
  // none to the server.
  const builder = props.isPastPlan
    ? "the LP solver, on the days and hours of the patient's existing visits,"
    : "the LP solver";
  const busy = approve.isPending || decline.isPending || regenerate.isPending;

  return (
    <VStack align="stretch" spacing={3} width="full">
      {declineForm.isOpen && (
        <Textarea
          isDisabled={busy}
          placeholder="Why are you declining this schedule? (optional)"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      )}
      {regenerateConfirm.isOpen && (
        <Alert status="warning">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">Build another schedule with the LP solver?</Text>
            <Text fontSize="sm">
              {canApprove
                ? `The schedule below is discarded, including any changes you made to it, ` +
                  `and ${builder} builds a new one from the same plan. Nothing about this ` +
                  `patient changes either way.`
                : `${capitalized(builder)} builds a new schedule from the same plan. ` +
                  `Nothing about this patient changes.`}
            </Text>
          </Box>
        </Alert>
      )}
      <HStack justify="space-between" spacing={3}>
        <HStack spacing={3}>
          {CAN_REGENERATE.includes(props.status) &&
            (regenerateConfirm.isOpen ? (
              <>
                <Button isDisabled={busy} variant="ghost" onClick={regenerateConfirm.onClose}>
                  Keep it
                </Button>
                <Button
                  colorScheme="orange"
                  isLoading={regenerate.isPending}
                  onClick={() =>
                    regenerate.mutate({
                      importId: props.importId,
                      scheduler: props.isPastPlan ? null : "solver-highs",
                    })
                  }
                >
                  Confirm regenerate
                </Button>
              </>
            ) : (
              <Button
                isDisabled={busy || declineForm.isOpen}
                variant="outline"
                onClick={regenerateConfirm.onOpen}
              >
                {props.isPastPlan ? "Regenerate" : "Regenerate by LP"}
              </Button>
            ))}
        </HStack>
        <HStack spacing={3}>
          {declineForm.isOpen ? (
            <>
              <Button isDisabled={busy} variant="ghost" onClick={declineForm.onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                isLoading={decline.isPending}
                onClick={() =>
                  decline.mutate({
                    importId: props.importId,
                    notes: notes.trim() === "" ? null : notes.trim(),
                  })
                }
              >
                Confirm decline
              </Button>
            </>
          ) : (
            canApprove && (
              <>
                <Button
                  colorScheme="red"
                  isDisabled={busy || regenerateConfirm.isOpen}
                  variant="outline"
                  onClick={declineForm.onOpen}
                >
                  Decline
                </Button>
                {props.approvalStart?.kind === "CHOICE" ? (
                  <Menu placement="top-end">
                    <MenuButton
                      as={Button}
                      colorScheme="blue"
                      isDisabled={regenerateConfirm.isOpen}
                      isLoading={approve.isPending}
                      rightIcon={<ChevronDownIcon />}
                    >
                      Approve schedule
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        onClick={() =>
                          approve.mutate({ importId: props.importId, startFrom: "IMMEDIATELY" })
                        }
                      >
                        {`Approve from today (${fmtDate(props.approvalStart.immediately)})`}
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          approve.mutate({ importId: props.importId, startFrom: "NEXT_SUNDAY" })
                        }
                      >
                        {`Approve from next Sunday (${fmtDate(props.approvalStart.nextSunday)})`}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                ) : (
                  <Button
                    colorScheme="blue"
                    isDisabled={regenerateConfirm.isOpen}
                    isLoading={approve.isPending}
                    // One day decides it: the plan's own future start, or today being a
                    // Sunday. The server resolves the same day for either choice.
                    onClick={() =>
                      approve.mutate({ importId: props.importId, startFrom: "IMMEDIATELY" })
                    }
                  >
                    {props.approvalStart?.kind === "FIXED"
                      ? `Approve (starts ${fmtDate(props.approvalStart.startDate)})`
                      : "Approve schedule"}
                  </Button>
                )}
              </>
            )
          )}
        </HStack>
      </HStack>
    </VStack>
  );
}

type CoServicePlanApprovalStart = NonNullable<
  ResponseOf<"get", "./co_service_plan_imports/:importId">["approvalStart"]
>;
type ApprovalStartFrom = BodyOf<"post", "./co_service_plan_imports/:importId/approve">["startFrom"];

/** The day an approval with `startFrom` takes effect, as the drawer was told. */
function approvalStartDate(
  offered: CoServicePlanApprovalStart | null,
  startFrom: ApprovalStartFrom
): LocalDate | null {
  if (offered === null) {
    return null;
  }
  if (offered.kind === "FIXED") {
    return offered.startDate;
  }
  return startFrom === "NEXT_SUNDAY" ? offered.nextSunday : offered.immediately;
}

type CoServicePlanImportStagedShift = components["schemas"]["CoServicePlanImportStagedShift"];
type CoServicePlanImportSchedule = components["schemas"]["CoServicePlanImportSchedule"];
type CoServicePlanImportTemplateShift = components["schemas"]["CoServicePlanImportTemplateShift"];
type CoServicePlanImportWeekVisit = components["schemas"]["CoServicePlanImportWeekVisit"];
type CoServicePlanImportHoursLine = components["schemas"]["CoServicePlanImportHoursLine"];
type CoServicePlanImportCaregiver = components["schemas"]["CoServicePlanImportCaregiver"];
type CoServicePlanImportCoordinatorEdit =
  components["schemas"]["CoServicePlanImportCoordinatorEdit"];
type CoServicePlanImportDuty = components["schemas"]["CoServicePlanImportDuty"];
type CoServicePlanImportLine = components["schemas"]["CoServicePlanImportLine"];
type CoServicePlanImportProposedChange = components["schemas"]["CoServicePlanImportProposedChange"];

/**
 * What the patient actually has this week, what their template says, and what the
 * import proposes.
 *
 * Side by side and on one row per weekday, because the question a coordinator is
 * actually answering is "what changes on Tuesday?" — and that is unanswerable from
 * lists read one after the other. The actual week leads because a template is only
 * what visits are generated from: a coordinator may since have moved, added or
 * removed some, and the visits are what the patient and caregiver are living with.
 */
function WeekComparison(props: {
  actual: CoServicePlanImportWeekVisit[];
  current: CoServicePlanImportTemplateShift[];
  suggested: CoServicePlanImportStagedShift[];
  weekStart: LocalDate;
}) {
  // A missed visit is shown, so the day reads as booked, but was never worked.
  const actualMinutes = props.actual
    .filter((visit) => !visit.missed)
    .reduce((total, visit) => total + visit.durationMinutes, 0);
  const currentMinutes = props.current.reduce((total, shift) => total + shift.durationMinutes, 0);
  const suggestedMinutes = props.suggested.reduce(
    (total, shift) => total + shift.durationMinutes,
    0
  );

  return (
    <Section title="Actual week vs template vs proposed week">
      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Day</Th>
              <Th>
                Actual this week
                <Text as="span" color="gray.500" fontWeight="normal">
                  {" "}
                  ({hours(actualMinutes)}/wk)
                </Text>
                <Text color="gray.500" fontWeight="normal" textTransform="none">
                  {dateFormatter.toDate(props.weekStart)} –{" "}
                  {dateFormatter.toDate(props.weekStart.plusDays(6))}
                </Text>
              </Th>
              <Th>
                Current template
                <Text as="span" color="gray.500" fontWeight="normal">
                  {" "}
                  ({hours(currentMinutes)}/wk)
                </Text>
              </Th>
              <Th>
                Proposed
                <Text as="span" color="gray.500" fontWeight="normal">
                  {" "}
                  ({hours(suggestedMinutes)}/wk)
                </Text>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {DAYS_MONDAY_FIRST.map((day, i) => {
              const actual = props.actual.filter((visit) => visit.dayOfWeek === day);
              const current = props.current.filter((shift) => shift.dayOfWeek === day);
              const suggested = props.suggested.filter((shift) => shift.dayOfWeek === day);
              if (actual.length === 0 && current.length === 0 && suggested.length === 0) {
                return null;
              }
              return (
                <Tr key={day}>
                  <Td fontWeight="semibold" verticalAlign="top">
                    {title(day)}
                    <Text color="gray.500" fontSize="xs" fontWeight="normal">
                      {dateFormatter.toDate(props.weekStart.plusDays(i))}
                    </Text>
                  </Td>
                  <Td verticalAlign="top">
                    {actual.length === 0 ? (
                      <Text color="gray.400">—</Text>
                    ) : (
                      actual.map((visit, j) => (
                        <Text key={j} color={visit.missed ? "gray.500" : undefined} fontSize="sm">
                          {visit.startTime} – {visit.endTime}
                          {visit.caregiverName !== null && ` · ${visit.caregiverName}`}
                          {visit.missed && (
                            <Badge colorScheme="red" ml={2}>
                              missed
                            </Badge>
                          )}
                        </Text>
                      ))
                    )}
                  </Td>
                  <Td verticalAlign="top">
                    {current.length === 0 ? (
                      <Text color="gray.400">—</Text>
                    ) : (
                      current.map((shift, j) => (
                        <Text key={j} fontSize="sm">
                          {shift.startTime} – {shift.endTime}
                          {shift.caregiverName !== null && ` · ${shift.caregiverName}`}
                          {/* A previous submission's shift is what an approval
                              replaces; a coordinator's own is left alone. */}
                          {shift.createdByImportId === null && (
                            <Badge colorScheme="purple" ml={2}>
                              coordinator
                            </Badge>
                          )}
                        </Text>
                      ))
                    )}
                  </Td>
                  <Td verticalAlign="top">
                    {suggested.length === 0 ? (
                      <Text color="gray.400">—</Text>
                    ) : (
                      suggested.map((shift) => (
                        <Text key={shift.index} fontSize="sm">
                          {shift.startTime} – {shift.endTime}
                          {shift.caregiverName !== null && ` · ${shift.caregiverName}`}
                        </Text>
                      ))
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
      {props.actual.length === 0 && (
        <Text color="gray.500" fontSize="sm" mt={3}>
          This patient has no visits this week.
        </Text>
      )}
      {props.current.length === 0 && (
        <Text color="gray.500" fontSize="sm" mt={3}>
          This patient has no weekly template today, so nothing is being replaced.
        </Text>
      )}
    </Section>
  );
}

/**
 * Authorized vs proposed vs asked-for.
 *
 * All three together because no one of them means much alone: a week at 40% of the
 * authorization is fine if that is what the plan asked for, and alarming if the
 * plan asked for the lot.
 *
 * Two questions on one table, and they are asked of different things. What is
 * still available is a question about an AUTHORIZATION, and a patient can hold
 * several on one service line, each with its own window and its own allowance.
 * Whether the patient gets the care the payer wrote down is a question about the
 * LINE, asked once however many authorizations happen to pay for it.
 *
 * So the per-authorization columns run down the left, a row each, and the line's
 * own pair sits on the right in cells spanning that line's rows. The spanning is
 * the point: printed on every row instead, "11:00 asked, 100% met" lands beside an
 * authorization whose own week is 00:00 and reads as a contradiction.
 */
function HoursComparison(props: { lines: CoServicePlanImportHoursLine[] }) {
  // One group per service line, in the order the lines first appear, each holding
  // its authorizations in the order they were given.
  //
  // Grouped by the PLAN's code rather than the authorization's, since "H0038:U1"
  // and "H0038:U1:HX" are one line asking once. An authorization the plan never
  // mentions is a line of its own, so it keeps its own dash instead of being
  // lumped in with every other unmentioned one.
  const groups = new Map<string, CoServicePlanImportHoursLine[]>();
  for (const line of props.lines) {
    const key = line.planServiceCode ?? `#${line.authorizationId ?? line.serviceCode}`;
    groups.set(key, [...(groups.get(key) ?? []), line]);
  }

  return (
    <Section title="Hours">
      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Authorization</Th>
              <Th isNumeric>Authorized / wk</Th>
              <Th isNumeric>Proposed / wk</Th>
              {/* Broken onto two lines on purpose: uppercased and letter-spaced,
                  these headers are wider than the numbers under them and push the
                  last column out of an 800px drawer. */}
              <Th isNumeric whiteSpace="normal">
                Utilization
                <br />
                (per auth)
              </Th>
              <Th isNumeric whiteSpace="normal">
                Plan asks
                <br />/ wk
              </Th>
              <Th isNumeric whiteSpace="normal">
                Utilization
                <br />
                (per plan)
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {[...groups.values()].flatMap((rows) =>
              rows.map((line, indexInLine) => {
                const asked = line.planRequiredWeeklyMinutes;
                // Flagged only when the plan quantified the line and the week
                // falls short of it. Over is not flagged: the verifier already
                // refuses a week that exceeds the authorization.
                const short =
                  line.planUtilizationPercent !== null && line.planUtilizationPercent < 100;
                return (
                  <Tr key={`${line.serviceCode}-${line.authorizationId ?? "none"}`}>
                    <Td fontWeight="semibold">
                      {line.serviceCode}
                      {line.periodType !== null && (
                        <Text as="span" color="gray.500" fontWeight="normal">
                          {" "}
                          ({line.periodType.toLowerCase().replace(/_/g, " ")})
                        </Text>
                      )}
                    </Td>
                    {/* Every authorization in one unit, whatever unit the payer
                        stated it in, so the column can be read down. */}
                    <Td isNumeric>
                      {line.authorizedWeeklyMinutes === null
                        ? "—"
                        : hours(line.authorizedWeeklyMinutes)}
                    </Td>
                    <Td isNumeric>{hours(line.suggestedWeeklyMinutes)}</Td>
                    <Td isNumeric>
                      {line.authorizationUtilizationPercent === null
                        ? "—"
                        : `${line.authorizationUtilizationPercent}%`}
                    </Td>
                    {/* The line's own pair, written once for the whole line. The
                        second is what says whether the patient gets the care the
                        payer wrote down; everything to its left only says whether
                        there is room for it. */}
                    {indexInLine === 0 && (
                      <>
                        <Td
                          isNumeric
                          borderLeftWidth="1px"
                          rowSpan={rows.length}
                          verticalAlign="middle"
                        >
                          {asked === null ? "—" : hours(asked)}
                        </Td>
                        <Td
                          isNumeric
                          color={short ? "orange.600" : undefined}
                          rowSpan={rows.length}
                          verticalAlign="middle"
                        >
                          {line.planUtilizationPercent === null
                            ? "—"
                            : `${line.planUtilizationPercent}%`}
                        </Td>
                      </>
                    )}
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>
      {props.lines.some(
        (line) => line.planUtilizationPercent !== null && line.planUtilizationPercent < 100
      ) && (
        <Text color="orange.600" fontSize="sm" mt={3}>
          A percentage in orange means the week schedules fewer hours than the service plan asked
          for on that line.
        </Text>
      )}
    </Section>
  );
}

/**
 * One shift, rendered the same way wherever a week is shown.
 *
 * `time` is a slot rather than two strings because the staged week and the model's
 * own snapshot write it differently. Everything else about the card is identical in
 * both, which is what keeps a refused week and an approved one reading alike.
 */
function ShiftCard(props: {
  dayOfWeek: string;
  time: React.ReactNode;
  caregiverId: number | null;
  caregiverName: string | null;
  authorizationId: number | null;
  duties: { dutyCode: number; occurrences: number }[];
  rationale?: string | null;
}) {
  const caregiver =
    props.caregiverName !== null && props.caregiverName !== ""
      ? props.caregiverName
      : props.caregiverId === null
      ? "Unassigned"
      : `Caregiver #${props.caregiverId}`;

  return (
    <Box borderWidth="1px" p={3} rounded="md">
      <Flex align="center" gap={3} wrap="wrap">
        <Tag colorScheme="blue">{title(props.dayOfWeek)}</Tag>
        {props.time}
        <Text color="gray.500" fontSize="sm">
          {caregiver}
          {props.authorizationId !== null && ` · Auth #${props.authorizationId}`}
        </Text>
      </Flex>
      <HStack mt={2} spacing={1} wrap="wrap">
        {props.duties.map((duty, index) => (
          // The count only where it says something. A duty performed once is the
          // ordinary case and printing "101 ×1" on every badge would bury the one
          // badge that means something different.
          <Badge key={`${index}-${duty.dutyCode}`} colorScheme="gray">
            {duty.occurrences > 1 ? `${duty.dutyCode} ×${duty.occurrences}` : duty.dutyCode}
          </Badge>
        ))}
      </HStack>
      {props.rationale !== null && props.rationale !== undefined && (
        <Text color="gray.600" fontSize="sm" mt={2}>
          {props.rationale}
        </Text>
      )}
    </Box>
  );
}

/**
 * The week, once: what will be written, and how to change it.
 *
 * These were two sections and said the same thing twice. The staged week IS the
 * week that will be written, so it is shown once, one card per shift — and where a
 * coordinator wants a different one, the editor opens on the whole week beside the
 * scheduler's original rather than trying to tell the difference here.
 *
 * `schedule` is the model's answer, and it is what a run that staged NOTHING has
 * to show: a refused week has a snapshot and no staged shifts, and it is still
 * the thing a coordinator needs to read.
 */
function ProposedWeek(props: {
  staged: CoServicePlanImportStagedShift[] | null;
  stagedAsGenerated: CoServicePlanImportStagedShift[] | null;
  schedule: CoServicePlanImportSchedule | null;
  caregivers: CoServicePlanImportCaregiver[];
  caregiverNames: Map<number, string>;
  coordinatorEdit: CoServicePlanImportCoordinatorEdit | null;
  duties: CoServicePlanImportDuty[];
  /** Which submission the editor's Submit replaces the week of. */
  importId: ExternalPatientServicePlanId;
  lines: CoServicePlanImportLine[];
  canModify: boolean;
  isPending: boolean;
}) {
  const editor = useDisclosure();

  const staged = props.staged ?? [];
  // A refused run staged nothing, and its snapshot is all there is to read.
  const modelOnly = props.staged === null || props.staged.length === 0;
  const count = modelOnly ? props.schedule?.shifts.length ?? 0 : staged.length;

  return (
    <Section
      action={
        props.isPending &&
        props.canModify &&
        !modelOnly && (
          <Button size="sm" variant="outline" onClick={editor.onOpen}>
            Edit schedule
          </Button>
        )
      }
      title={`Week the scheduler built (${count} ${count === 1 ? "shift" : "shifts"})`}
    >
      {/* Mounted only while it is open: the draft inside is seeded from the staged
          week on mount, so reopening always starts from what is staged now. */}
      {editor.isOpen && (
        <CoServicePlanScheduleEditorModal
          caregivers={props.caregivers}
          coordinatorEdit={props.coordinatorEdit}
          duties={props.duties}
          importId={props.importId}
          lines={props.lines}
          original={props.stagedAsGenerated}
          staged={staged}
          onClose={editor.onClose}
        />
      )}
      {props.schedule?.failed === true && (
        <Alert mb={3} status="error">
          <AlertIcon />
          This week was refused — it is kept only so the refusal can be read.
        </Alert>
      )}
      {props.schedule?.notes !== null && props.schedule?.notes !== undefined && (
        <Text color="gray.600" fontSize="sm" mb={3} whiteSpace="pre-wrap">
          {props.schedule.notes}
        </Text>
      )}
      {!modelOnly && (
        <Text color="gray.600" fontSize="sm" mb={3}>
          {props.isPending
            ? "These are the times that will be written when this is approved."
            : "These are the times that were written."}
        </Text>
      )}
      <VStack align="stretch" spacing={3}>
        {modelOnly
          ? [...(props.schedule?.shifts ?? [])]
              .sort((a, b) => dayIndex(a.dayOfWeek) - dayIndex(b.dayOfWeek))
              .map((shift, i) => (
                <ShiftCard
                  key={i}
                  authorizationId={shift.authorizationId}
                  caregiverId={shift.caregiverId}
                  // The snapshot stores an id; the name comes from the caregivers
                  // the submission itself named.
                  caregiverName={
                    shift.caregiverId === null
                      ? null
                      : props.caregiverNames.get(shift.caregiverId) ?? null
                  }
                  dayOfWeek={shift.dayOfWeek}
                  duties={shift.duties}
                  rationale={shift.rationale}
                  time={
                    <Text fontWeight="semibold">
                      {shift.startTime} – {shift.endTime}
                    </Text>
                  }
                />
              ))
          : [...staged]
              .sort((a, b) => dayIndex(a.dayOfWeek) - dayIndex(b.dayOfWeek))
              .map((shift) => (
                <ShiftCard
                  key={shift.index}
                  authorizationId={shift.authorizationId}
                  caregiverId={shift.caregiverId}
                  caregiverName={shift.caregiverName}
                  dayOfWeek={shift.dayOfWeek}
                  duties={shift.duties}
                  rationale={shift.rationale}
                  time={
                    <Text fontWeight="semibold">
                      {shift.startTime} – {shift.endTime}
                    </Text>
                  }
                />
              ))}
      </VStack>
    </Section>
  );
}

function DrawerInner(props: { importId: ExternalPatientServicePlanId }) {
  const { queries } = useApi();
  const query = useQuery(queries.servicePlan.coImportDetails(props.importId));

  if (query.isLoading) {
    return (
      <DrawerBody>
        <VStack align="stretch" pt={12} spacing={4}>
          <Skeleton height="60px" />
          <Skeleton height="200px" />
          <Skeleton height="200px" />
        </VStack>
      </DrawerBody>
    );
  }

  if (query.data === undefined) {
    return (
      <DrawerBody>
        <Alert status="error">
          <AlertIcon />
          Could not load this import.
        </Alert>
      </DrawerBody>
    );
  }

  const details = query.data;
  const schedule = details.schedule;
  const isPastPlan = details.planKind === "PAST";
  // A past plan's history plan of care is applied on its own, before any new week
  // exists; `planOfCareId` is that week's. Past-only means there is no week.
  const historyApplied = isPastPlan && details.pastPlanOfCareId !== null;
  const pastOnly = isPastPlan && details.planOfCareId === null;
  const stagedShifts = details.stagedShifts;
  // Keyed by plain number: the snapshot stores a bare id, while the submission's
  // caregivers carry the branded one.
  const caregiverNames = new Map<number, string>(
    details.caregivers.map((caregiver) => [
      Number(caregiver.caregiverId),
      `${caregiver.firstName ?? ""} ${caregiver.lastName ?? ""}`.trim(),
    ])
  );
  const hasStagedShifts = stagedShifts !== null && stagedShifts.length > 0;
  const errors = schedule?.violations.filter((x) => x.severity === "ERROR") ?? [];
  const warnings = schedule?.violations.filter((x) => x.severity !== "ERROR") ?? [];
  // One permission covers every way a coordinator can act on a submission —
  // editing its week, approving it, declining it. Read once so the footer
  // and the copy that describes it can never disagree.
  const canReview = auth.isAllowedTo("service_plan_import_edit");

  return (
    <>
      <DrawerHeader borderBottomWidth="1px">
        <HStack spacing={3}>
          <Text>
            {details.patientFirstName} {details.patientLastName}
          </Text>
          <CoServicePlanImportStatusTag status={details.status} />
          {isPastPlan && (
            <Tooltip label="Past service plan: its plan of care is applied to the patient's history as sent and, unless a newer plan governs, a new schedule is proposed for review, taking effect today or from the next Sunday as chosen at approval">
              <Tag colorScheme="purple" variant="subtle">
                Past service plan
              </Tag>
            </Tooltip>
          )}
          {/* Beside the status, because "what state is this in" and "what built it"
              are the two things a coordinator opening the drawer wants first. */}
          <CoServicePlanEngineTag engine={schedule?.engine} />
          {/* And immediately after it, because the engine badge alone would say a
              machine built the week below when a person rewrote it. */}
          {details.coordinatorEdit !== null && (
            <Tooltip label="A coordinator replaced the week this engine built">
              <Tag colorScheme="purple" variant="subtle">
                Edited
              </Tag>
            </Tooltip>
          )}
        </HStack>
        <Text color="gray.500" fontSize="sm" fontWeight="normal">
          {details.submissionId}
          {details.planLogId !== null && ` · ${details.planLogId}`}
          {details.planVersion !== null && ` v${details.planVersion}`}
        </Text>
      </DrawerHeader>

      <DrawerBody>
        <VStack align="stretch" divider={<Divider />} spacing={6}>
          {details.status === "PENDING_APPROVAL" && (
            <Alert status="info">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">
                  {canReview
                    ? "This schedule is waiting for your approval."
                    : "This schedule is waiting for approval."}
                </Text>
                <Text fontSize="sm">
                  {isPastPlan
                    ? "Its history plan of care is already in force for this patient's visits. " +
                      "No weekly template or visits have been created from this plan yet. " +
                      "Approving creates them from the day you choose — today or the next " +
                      "Sunday — replaces this patient's visits on the plan's service lines from " +
                      "that day on, whoever built them, and ends the history plan of care the " +
                      "day before."
                    : "Nothing has changed for this patient yet — no weekly template and no " +
                      "visits exist. Approving creates them and replaces this patient's whole " +
                      "schedule from the day it takes effect, whoever built it."}{" "}
                  Declining changes nothing, and regenerating asks for a different schedule from the
                  same plan.
                  {!canReview && " You do not have permission to act on this submission."}
                </Text>
              </Box>
            </Alert>
          )}
          {details.status === "COMPLETED_WITH_WARNINGS" && (
            <Alert status="warning">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">
                  {pastOnly
                    ? "This plan of care was applied, with caveats."
                    : "This schedule was written, with caveats."}
                </Text>
                <Text fontSize="sm">
                  {pastOnly
                    ? "It is in force for the patient's visits; the caveats below are about what " +
                      "it could not carry over."
                    : "It is live on the patient's calendar, but it is not everything that was " +
                      "asked for."}
                  {isPastPlan &&
                    !pastOnly &&
                    " Its history plan of care was applied too; some caveats below may be about it."}
                </Text>
                {details.job?.message !== null && details.job?.message !== undefined && (
                  <VStack align="stretch" mt={2} spacing={1}>
                    {details.job.message.split(" | ").map((line, i) => (
                      <Text key={i} fontSize="sm">
                        • {line}
                      </Text>
                    ))}
                  </VStack>
                )}
              </Box>
            </Alert>
          )}
          {details.status === "DECLINED" && (
            <Alert status="warning">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">This schedule was declined.</Text>
                <Text fontSize="sm">
                  {historyApplied
                    ? "The new schedule was declined. This plan's history plan of care " +
                      "stays in force for the patient's visits; nothing else was changed. The " +
                      "proposal below is kept for the record."
                    : "It was never applied, so the patient was left exactly as they were. The " +
                      "proposal below is kept for the record."}
                  {canReview && " Regenerating builds another one from the same plan."}
                </Text>
              </Box>
            </Alert>
          )}
          {details.status === "NEEDS_REVIEW" && (
            <Alert status="error">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">
                  {historyApplied
                    ? "The history plan of care is in force, but no new schedule was produced."
                    : isPastPlan
                    ? "No plan of care was produced."
                    : "No schedule was produced."}
                </Text>
                <Text fontSize="sm">
                  {historyApplied
                    ? "The plan is stored and its history is applied; no new schedule was " +
                      "written. What went wrong is below under Job and, where the scheduler " +
                      "answered at all, under Findings."
                    : isPastPlan
                    ? "The plan is stored and nothing was applied, so this patient is exactly as " +
                      "they were. What went wrong is below under Job."
                    : "The plan is stored and nothing was applied, so this patient is exactly as " +
                      "they were. What went wrong is below under Job and, where the scheduler " +
                      "answered at all, under Findings."}
                  {!canReview
                    ? " You do not have permission to act on this submission."
                    : pastOnly && !historyApplied
                    ? " Fix what it names and regenerate to try again."
                    : " Fix what it names — an authorization, a caregiver's validated duties —" +
                      " and regenerate to try again."}
                </Text>
              </Box>
            </Alert>
          )}
          {details.status === "BLOCKED_BY_REVIEW" && (
            <Alert status="warning">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Waiting on an earlier submission.</Text>
                <Text fontSize="sm">
                  This patient has another submission still awaiting review. This one will be
                  scheduled once that one is approved or declined.
                </Text>
              </Box>
            </Alert>
          )}
          <Section title="Submission">
            <Facts
              facts={[
                ["Office", details.officeName ?? "—"],
                ["Received", dateFormatter.toDateTime(details.receivedAt)],
                ["Finalized by sender", dateFormatter.toDateTime(details.finalizedAt)],
                [
                  "Effective",
                  details.startDate === null && details.endDate === null
                    ? "Left to the authorizations"
                    : `${fmtDate(details.startDate)} – ${fmtDate(details.endDate)}`,
                ],
                ...(isPastPlan
                  ? [
                      [
                        "History plan of care",
                        details.pastPlanOfCareId === null ? "—" : `#${details.pastPlanOfCareId}`,
                      ] satisfies [string, string],
                      [
                        "New plan of care",
                        details.planOfCareId === null ? "—" : `#${details.planOfCareId}`,
                      ] satisfies [string, string],
                    ]
                  : [
                      [
                        "Plan of care",
                        details.planOfCareId === null ? "—" : `#${details.planOfCareId}`,
                      ] satisfies [string, string],
                    ]),
                ["Duties sent", String(details.duties.length)],
                ["Caregivers named", String(details.caregivers.length)],
              ]}
            />
            {details.planNotes !== null && (
              <Box bg="gray.50" mt={4} p={3} rounded="md">
                <Text color="gray.500" fontSize="xs" mb={1}>
                  Plan notes from the sender
                </Text>
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {details.planNotes}
                </Text>
              </Box>
            )}
          </Section>

          <Section title="Job">
            {details.job === null ? (
              <Text color="gray.500">No scheduling job was ever queued for this submission.</Text>
            ) : (
              <>
                <Facts
                  facts={[
                    ["Queued", dateFormatter.toDateTime(details.job.createdAt)],
                    ["Started", fmtDateTime(details.job.startedAt)],
                    ["Ended", fmtDateTime(details.job.endedAt)],
                    ["Retries", `${details.job.retryCount} of ${details.job.maxRetries}`],
                    ["Scheduler calls", schedule === null ? "—" : String(schedule.attempts)],
                  ]}
                />
                {details.job.message !== null && details.status !== "COMPLETED_WITH_WARNINGS" && (
                  // Carries warnings on a schedule that was written and the reason
                  // on one that was not, so it is shown either way — except where
                  // the alert at the top of the drawer has already printed these
                  // very lines, which is what COMPLETED_WITH_WARNINGS does. One
                  // list of caveats twice on one screen reads as two problems.
                  <Alert mt={4} status={details.status === "NEEDS_REVIEW" ? "error" : "warning"}>
                    <AlertIcon />
                    <VStack align="stretch" spacing={1}>
                      {details.job.message.split(" | ").map((line, i) => (
                        <Text key={i} fontSize="sm">
                          {line}
                        </Text>
                      ))}
                    </VStack>
                  </Alert>
                )}
              </>
            )}
          </Section>

          {errors.length > 0 && (
            <Section title={`Blocking findings (${errors.length})`}>
              <ServicePlanViolationList violations={errors} />
            </Section>
          )}

          {warnings.length > 0 && (
            <Section title={`Warnings (${warnings.length})`}>
              <ServicePlanViolationList violations={warnings} />
            </Section>
          )}

          {schedule?.noteRestrictions != null && (
            <NoteRestrictionsSection noteRestrictions={schedule.noteRestrictions} />
          )}

          {schedule !== null && schedule.unstaffed.length > 0 && (
            <Section title={`Left unstaffed (${schedule.unstaffed.length})`}>
              <VStack align="stretch" spacing={2}>
                {schedule.unstaffed.map((x, i) => (
                  <HStack key={i} align="start" spacing={3}>
                    <Badge colorScheme="red">
                      {x.dutyCode === null ? "—" : `Duty ${x.dutyCode}`}
                    </Badge>
                    {x.dayOfWeek !== null && <Tag size="sm">{title(x.dayOfWeek)}</Tag>}
                    <Text fontSize="sm">{x.reason}</Text>
                  </HStack>
                ))}
              </VStack>
            </Section>
          )}

          {schedule !== null && schedule.proposedChanges.length > 0 && (
            <Section
              title={
                // The changes describe the week as first proposed; a coordinator's edit
                // since then is not reflected in them.
                details.coordinatorEdit !== null
                  ? `Proposed changes (as first proposed, ${schedule.proposedChanges.length})`
                  : `Proposed changes (${schedule.proposedChanges.length})`
              }
            >
              <VStack align="stretch" spacing={2}>
                {schedule.proposedChanges.map((change, i) => (
                  <HStack key={i} align="start" spacing={3}>
                    <Badge colorScheme={CHANGE_KIND_COLORS[change.kind]}>
                      {changeLabel(change)}
                    </Badge>
                    <Text fontSize="sm">{change.message}</Text>
                  </HStack>
                ))}
              </VStack>
            </Section>
          )}

          {/* Pending only: once applied, the "current" template IS the proposed one
              and the comparison says nothing. */}
          {details.status === "PENDING_APPROVAL" && details.stagedShifts !== null && (
            <WeekComparison
              actual={details.currentWeekVisits}
              current={details.currentTemplate}
              suggested={details.stagedShifts}
              weekStart={details.currentWeekStart}
            />
          )}

          {details.status === "PENDING_APPROVAL" && details.hours.length > 0 && (
            <HoursComparison lines={details.hours} />
          )}

          {details.coordinatorEdit !== null && (
            <CoordinatorEditSection coordinatorEdit={details.coordinatorEdit} />
          )}

          {/* One week, not two lists of the same week: the card carries the time
              that will be written, and the editor is where a different one is
              built. */}
          {(hasStagedShifts || schedule !== null) && (
            <ProposedWeek
              canModify={canReview}
              caregiverNames={caregiverNames}
              caregivers={details.caregivers}
              coordinatorEdit={details.coordinatorEdit}
              duties={details.duties}
              importId={props.importId}
              isPending={details.status === "PENDING_APPROVAL"}
              lines={details.lines}
              schedule={schedule}
              staged={stagedShifts}
              stagedAsGenerated={details.stagedAsGenerated}
            />
          )}

          <Section title={`Caregivers named (${details.caregivers.length})`}>
            {details.caregivers.length === 0 ? (
              <Text color="gray.500">The sender named no caregivers.</Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                {details.caregivers.map((caregiver) => (
                  <Box key={caregiver.caregiverId} borderWidth="1px" p={3} rounded="md">
                    <Text fontWeight="semibold">
                      {caregiver.firstName} {caregiver.lastName}{" "}
                      <Text as="span" color="gray.500" fontWeight="normal">
                        #{caregiver.caregiverId}
                      </Text>
                    </Text>
                    <HStack mt={2} spacing={1} wrap="wrap">
                      {caregiver.validatedDuties.map((duty, i) => (
                        <Badge key={i} colorScheme="green">
                          {duty.serviceCode} · {duty.dutyCode}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </Section>

          <Section title={`Duties sent (${details.duties.length})`}>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Service</Th>
                    <Th>Duty</Th>
                    <Th>Days</Th>
                    <Th isNumeric>Min/task</Th>
                    <Th isNumeric>Min/day</Th>
                    <Th isNumeric>Min/week</Th>
                    <Th isNumeric>×/day</Th>
                    <Th isNumeric>×/week</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {details.duties.map((duty, i) => (
                    <Tr key={i}>
                      <Td>{duty.serviceCode}</Td>
                      <Td>
                        <Text>{duty.label ?? "Unmapped"}</Text>
                        <Text color="gray.500" fontSize="xs">
                          {duty.dutyCode}
                          {duty.isRequired === true && " · required"}
                        </Text>
                      </Td>
                      <Td>{fmtDays(duty.daysOfWeek)}</Td>
                      <Td isNumeric>{duty.minutesPerTask ?? "—"}</Td>
                      <Td isNumeric>{duty.minutesPerDay ?? "—"}</Td>
                      <Td isNumeric>{duty.minutesPerWeek ?? "—"}</Td>
                      <Td isNumeric>{duty.timesPerDay ?? "—"}</Td>
                      <Td isNumeric>{duty.timesPerWeek ?? "—"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Section>
        </VStack>
      </DrawerBody>

      {canReview && CAN_REGENERATE.includes(details.status) && (
        <DrawerFooter borderTopWidth="1px">
          <ReviewActions
            approvalStart={details.approvalStart}
            importId={props.importId}
            isPastPlan={isPastPlan}
            status={details.status}
          />
        </DrawerFooter>
      )}
    </>
  );
}

function Section(props: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box>
      <Flex align="center" gap={3} justify="space-between" mb={3} minH="32px">
        <Heading size="sm">{props.title}</Heading>
        {props.action}
      </Flex>
      {props.children}
    </Box>
  );
}

function Facts(props: { facts: [string, string][] }) {
  return (
    <Box display="grid" gap={3} gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))">
      {props.facts.map(([label, value]) => (
        <Box key={label}>
          <Text color="gray.500" fontSize="xs">
            {label}
          </Text>
          <Text fontSize="sm">{value}</Text>
        </Box>
      ))}
    </Box>
  );
}

/**
 * What the scheduler made of the plan's written notes.
 *
 * Both halves are shown, and the ignored one is not hidden behind a toggle: a
 * coordinator who wrote "ask for Maria" and got somebody else needs to find out here
 * that Maria is not on this plan. Each applied restriction carries the words it came
 * from, so the reading can be checked against what was actually written.
 */
function NoteRestrictionsSection(props: {
  noteRestrictions: {
    model: string | null;
    applied: {
      kind: string;
      description: string;
      quote: string;
      dayOfWeek: string | null;
      caregiverId: number | null;
      serviceCode: string | null;
      dutyCode: number | null;
    }[];
    ignored: { text: string; reason: string }[];
  };
}) {
  const { applied, ignored, model } = props.noteRestrictions;

  if (applied.length === 0 && ignored.length === 0) {
    return null;
  }

  return (
    <Section title={`Read from the plan notes (${applied.length})`}>
      <VStack align="stretch" spacing={2}>
        {applied.map((restriction, i) => (
          <Box key={i} borderWidth="1px" p={3} rounded="md">
            <HStack spacing={2} wrap="wrap">
              <Badge colorScheme="teal">{restriction.kind}</Badge>
              {restriction.dayOfWeek !== null && (
                <Tag size="sm">{title(restriction.dayOfWeek)}</Tag>
              )}
              {restriction.serviceCode !== null && <Tag size="sm">{restriction.serviceCode}</Tag>}
              {restriction.dutyCode !== null && <Tag size="sm">Duty {restriction.dutyCode}</Tag>}
              {restriction.caregiverId !== null && (
                <Tag size="sm">Caregiver #{restriction.caregiverId}</Tag>
              )}
            </HStack>
            <Text fontSize="sm" mt={2}>
              {restriction.description}
            </Text>
            <Text color="gray.600" fontSize="xs" fontStyle="italic" mt={1}>
              “{restriction.quote}”
            </Text>
          </Box>
        ))}

        {ignored.length > 0 && (
          <Alert alignItems="start" status="warning">
            <AlertIcon />
            <VStack align="stretch" spacing={1}>
              <Text fontSize="sm" fontWeight="semibold">
                Not applied ({ignored.length})
              </Text>
              {ignored.map((entry, i) => (
                <Text key={i} fontSize="sm">
                  “{entry.text}” — {entry.reason}
                </Text>
              ))}
            </VStack>
          </Alert>
        )}

        {model !== null && (
          <Text color="gray.500" fontSize="xs">
            Read by {model}
          </Text>
        )}
      </VStack>
    </Section>
  );
}

/**
 * Who replaced this week, and what they accepted to do it.
 *
 * On the drawer rather than only in the editor, because the person who APPROVES a
 * submission is usually not the person who edited it: approving is the moment the
 * patient's calendar changes, and whoever presses it should be able to read what was
 * overruled to get here before they do.
 *
 * The findings are the ones shown at submit time and proceeded past — a week that
 * was clean when it was submitted records an empty list, which is a fact worth
 * printing rather than an empty section.
 */
function CoordinatorEditSection(props: { coordinatorEdit: CoServicePlanImportCoordinatorEdit }) {
  const { acceptedViolations, editedAt, editedByName } = props.coordinatorEdit;
  const errors = acceptedViolations.filter((violation) => violation.severity === "ERROR");
  const warnings = acceptedViolations.filter((violation) => violation.severity !== "ERROR");

  return (
    <Section title="Edited by a coordinator">
      <Text color="gray.600" fontSize="sm" mb={3}>
        The week below was replaced by {editedByName ?? "a coordinator who has since been removed"}{" "}
        on {dateFormatter.toDateTime(editedAt)}. It is no longer the week the engine built — that
        one is kept, and the editor still shows it beside this.
      </Text>
      {acceptedViolations.length === 0 ? (
        <Text color="gray.500" fontSize="sm">
          Nothing was flagged against it when it was submitted.
        </Text>
      ) : (
        <VStack align="stretch" spacing={4}>
          {errors.length > 0 && (
            <Box borderLeftColor="red.500" borderLeftWidth="4px" pl={4}>
              <Text color="red.600" fontWeight="semibold" mb={2}>
                Blocking findings accepted ({errors.length})
              </Text>
              <ServicePlanViolationList violations={errors} />
            </Box>
          )}
          {warnings.length > 0 && (
            <Box borderLeftColor="orange.400" borderLeftWidth="4px" pl={4}>
              <Text color="orange.600" fontWeight="semibold" mb={2}>
                Warnings accepted ({warnings.length})
              </Text>
              <ServicePlanViolationList violations={warnings} />
            </Box>
          )}
        </VStack>
      )}
    </Section>
  );
}

function fmtDate(date: LocalDate | null) {
  return date === null ? "—" : dateFormatter.toDate(date);
}

function fmtDateTime(instant: Instant | null) {
  return instant === null ? "—" : dateFormatter.toDateTime(instant);
}

/** The sender's own 0 = Sunday encoding, which is also the order they read in. */
function fmtDays(days: number[] | null) {
  if (days === null || days.length === 0) {
    return "—";
  }

  return [...days]
    .sort((a, b) => a - b)
    .map((day) => title(DAYS[day] ?? "?").slice(0, 3))
    .join(", ");
}
